const DIAL_CODES = {
    AT: { countryName: 'Austria', dialCode: '+43' },
    CH: { countryName: 'Switzerland', dialCode: '+41' },
    DE: { countryName: 'Germany', dialCode: '+49' },
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatClock = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const formatAttemptAt = (date) => {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()} · ${hours}:${minutes}`;
};

const addMinutes = (date, minutes) => new Date(date.getTime() + (minutes * 60000));

const parseSubmittedAt = (value) => {
    const parsed = new Date(String(value || '').replace('·', ''));

    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

const phoneDetails = ({
    company,
    country,
    reputation,
}) => {
    const dial = DIAL_CODES[country] || {
        countryName: company.country,
        dialCode: '',
    };

    return {
        countryCode: country,
        countryName: dial.countryName,
        dialCode: dial.dialCode,
        format: 'Valid',
        formatStatus: 'valid',
        number: company.phone,
        reputation,
        source: 'Company imprint',
        type: 'Business (landline)',
    };
};

const checklist = (callStatus, contactStatus) => ([
    { id: 'format', label: 'Number format is valid', status: 'valid' },
    { id: 'country', label: 'Country code matches company', status: 'valid' },
    { id: 'call', label: 'Call verification successful', status: callStatus },
    { id: 'contact', label: 'Contact person confirmed', status: contactStatus },
]);

const createAttempt = ({
    analysis,
    at,
    durationSeconds,
    id,
    status,
    transcript,
}) => ({
    analysis,
    at: formatAttemptAt(at),
    durationLabel: formatClock(durationSeconds),
    durationSeconds,
    id,
    label: `Attempt ${id}`,
    status,
    transcript,
});

const verifiedScenario = ({
    company,
    country,
    freightReference,
    submittedAt,
}) => {
    const startedAt = addMinutes(parseSubmittedAt(submittedAt), 4);
    const { legalName, phone } = company;

    return {
        alert: {
            color: 'success',
            description: `An outbound call to ${legalName} on ${phone} was answered. The switchboard confirmed the legal entity and freight ${freightReference}.`,
            icon: 'ri-checkbox-circle-fill',
            metrics: [
                { label: 'Risk level', value: 'Low', type: 'risk' },
                { label: 'Attempts', value: 1 },
                { label: 'Last attempt', value: formatAttemptAt(startedAt) },
                { label: 'Recommendation', value: 'Proceed', className: 'text-success' },
            ],
            title: 'Phone number verified',
        },
        attempts: [
            createAttempt({
                analysis: `${legalName} answered on its published switchboard. The contact confirmed the legal entity and freight ${freightReference}, so the human identity check passed.`,
                at: startedAt,
                durationSeconds: 84,
                id: 1,
                status: 'answered',
                transcript: [
                    { at: '00:00', text: `Calling ${phone}` },
                    { at: '00:06', text: 'Ringing' },
                    { at: '00:14', text: `Answered by the ${legalName} switchboard` },
                    { at: '00:31', text: `Contact confirmed ${legalName}` },
                    { at: '00:58', text: `Freight ${freightReference} confirmed` },
                    { at: '01:24', text: 'Call ended' },
                ],
            }),
        ],
        checklist: checklist('verified', 'verified'),
        nextSteps: [
            {
                action: 'Done',
                description: 'The published switchboard confirmed the company. No further call is required.',
                icon: 'ri-checkbox-circle-line',
                id: 'confirmed',
                title: 'Human identity confirmed',
            },
            {
                action: 'Download',
                description: 'Keep the answered call with the bid report.',
                icon: 'ri-download-2-line',
                id: 'recording',
                title: 'Recording stored with this bid',
            },
        ],
        phone: phoneDetails({ company, country, reputation: 'verified' }),
    };
};

const rejectedScenario = ({
    company,
    country,
    submittedAt,
}) => {
    const firstAt = addMinutes(parseSubmittedAt(submittedAt), 7);
    const secondAt = addMinutes(firstAt, 16);
    const { legalName, phone } = company;

    return {
        alert: {
            color: 'danger',
            description: `Outbound calls to ${legalName} on ${phone} did not reach a person who could confirm the company. Human identity could not be established.`,
            icon: 'ri-close-circle-fill',
            metrics: [
                { label: 'Risk level', value: 'High', type: 'risk' },
                { label: 'Attempts', value: 2 },
                { label: 'Last attempt', value: formatAttemptAt(secondAt) },
                { label: 'Recommendation', value: 'Do not proceed', className: 'text-danger' },
            ],
            title: 'Phone number could not be verified',
        },
        attempts: [
            createAttempt({
                analysis: `The call to ${phone} rang out. Nobody at ${legalName} answered, so this attempt did not confirm a contact.`,
                at: firstAt,
                durationSeconds: 28,
                id: 1,
                status: 'not_answered',
                transcript: [
                    { at: '00:00', text: `Calling ${phone}` },
                    { at: '00:04', text: 'Ringing' },
                    { at: '00:28', text: 'No answer. Call ended.' },
                ],
            }),
            createAttempt({
                analysis: `${phone} reached a generic voicemail that did not name ${legalName}. No business contact was confirmed.`,
                at: secondAt,
                durationSeconds: 41,
                id: 2,
                status: 'voicemail',
                transcript: [
                    { at: '00:00', text: `Calling ${phone}` },
                    { at: '00:05', text: 'Ringing' },
                    { at: '00:19', text: 'Voicemail greeting, company name not stated' },
                    { at: '00:41', text: 'Call ended' },
                ],
            }),
        ],
        checklist: checklist('failed', 'not_verified'),
        nextSteps: [
            {
                action: 'Reject bid',
                description: `Do not award freight to ${legalName} while the published number cannot confirm a contact.`,
                icon: 'ri-close-circle-line',
                id: 'reject',
                title: 'Hold this carrier',
            },
            {
                action: 'Request info',
                description: 'Ask the carrier for a direct business number that a named contact will answer.',
                icon: 'ri-phone-line',
                id: 'alternative',
                title: 'Request a direct business number',
            },
            {
                action: 'Escalate',
                description: 'Send the unanswered calls and voicemail to an analyst before any award.',
                icon: 'ri-search-eye-line',
                id: 'escalate',
                title: 'Escalate to manual review',
            },
        ],
        phone: phoneDetails({ company, country, reputation: 'not_verified' }),
    };
};

const actionRequiredScenario = ({
    company,
    country,
    submittedAt,
}) => {
    const firstAt = addMinutes(parseSubmittedAt(submittedAt), 6);
    const secondAt = addMinutes(firstAt, 18);
    const { email, legalName, phone } = company;
    const mailbox = email && email !== '—' ? email : null;

    return {
        alert: {
            color: 'warning',
            description: `We called ${legalName} on ${phone}. Neither attempt was answered, so no contact person could be confirmed.`,
            icon: 'ri-error-warning-fill',
            metrics: [
                { label: 'Risk level', value: 'Medium', type: 'risk' },
                { label: 'Attempts', value: 2 },
                { label: 'Last attempt', value: formatAttemptAt(secondAt) },
                { label: 'Recommendation', value: 'Request a valid business number', className: 'text-warning' },
            ],
            title: 'Phone number could not be verified',
        },
        attempts: [
            createAttempt({
                analysis: `${phone} was not answered. The attempt ended without reaching ${legalName}.`,
                at: firstAt,
                durationSeconds: 28,
                id: 1,
                status: 'not_answered',
                transcript: [
                    { at: '00:00', text: `Calling ${phone}` },
                    { at: '00:03', text: 'Ringing' },
                    { at: '00:28', text: 'No answer. Call ended.' },
                ],
            }),
            createAttempt({
                analysis: `The second call to ${phone} was not answered and reached voicemail. No one confirmed they represent ${legalName}.`,
                at: secondAt,
                durationSeconds: 36,
                id: 2,
                status: 'not_answered',
                transcript: [
                    { at: '00:00', text: `Calling ${phone}` },
                    { at: '00:04', text: 'Ringing' },
                    { at: '00:27', text: 'No answer' },
                    { at: '00:36', text: 'Call ended' },
                ],
            }),
        ],
        checklist: checklist('not_verified', 'not_checked'),
        nextSteps: [
            {
                action: 'Request info',
                description: `Ask ${legalName} for a business number that a named contact will answer.`,
                icon: 'ri-phone-line',
                id: 'alternative',
                title: 'Request an alternative business number',
            },
            {
                action: 'Send email',
                description: mailbox
                    ? `Confirm ${phone} from the published mailbox ${mailbox}.`
                    : `Confirm ${phone} from the address published on the company imprint.`,
                icon: 'ri-mail-line',
                id: 'email',
                title: 'Verify the contact by email',
            },
            {
                action: 'Escalate',
                description: 'If the number still cannot be confirmed, hand the bid to an analyst.',
                icon: 'ri-user-search-line',
                id: 'escalate',
                title: 'Escalate to manual review',
            },
        ],
        phone: phoneDetails({ company, country, reputation: 'not_verified' }),
    };
};

const verifyingScenario = ({
    company,
    country,
    submittedAt,
}) => {
    const startedAt = addMinutes(parseSubmittedAt(submittedAt), 6);
    const { legalName, phone } = company;

    return {
        alert: {
            color: 'info',
            description: `An outbound call to ${legalName} on ${phone} is still in progress. Human identity will update when the call ends.`,
            icon: 'ri-time-line',
            metrics: [
                { label: 'Risk level', value: 'Medium', type: 'risk' },
                { label: 'Attempts', value: 1 },
                { label: 'Last attempt', value: formatAttemptAt(startedAt) },
                { label: 'Recommendation', value: 'Wait for completion', className: 'text-info' },
            ],
            title: 'Call verification in progress',
        },
        attempts: [
            createAttempt({
                analysis: `The call to ${phone} is still ringing. Nobody at ${legalName} has confirmed the company yet.`,
                at: startedAt,
                durationSeconds: 18,
                id: 1,
                status: 'in_progress',
                transcript: [
                    { at: '00:00', text: `Calling ${phone}` },
                    { at: '00:05', text: 'Ringing' },
                    { at: '00:18', text: 'Still ringing' },
                ],
            }),
        ],
        checklist: checklist('not_checked', 'not_checked'),
        nextSteps: [
            {
                action: 'In progress',
                description: 'The outbound call is still open. The result will appear on this attempt.',
                icon: 'ri-time-line',
                id: 'wait',
                title: 'Wait for the call to finish',
            },
            {
                action: 'Request info',
                description: `If ${legalName} does not answer, ask for a direct line.`,
                icon: 'ri-phone-line',
                id: 'fallback',
                title: 'Prepare a fallback number',
            },
        ],
        phone: phoneDetails({ company, country, reputation: 'not_checked' }),
    };
};

const scenarios = {
    Verified: verifiedScenario,
    Rejected: rejectedScenario,
    'Action Required': actionRequiredScenario,
    Verifying: verifyingScenario,
};

const createCallVerification = ({
    company,
    country,
    freightReference,
    status,
    submittedAt,
}) => {
    const build = scenarios[status] || verifyingScenario;

    return build({
        company,
        country,
        freightReference,
        submittedAt,
    });
};

export default createCallVerification;
